// QR rendering for operator claim codes.
//
// `qrcode-generator` is a dependency-free encoder that hands back a module
// matrix; it can emit its own GIF data URL, but the brief asks for a PNG, so we
// draw the matrix onto a canvas and export image/png. That also lets us control
// the quiet zone and pixel scale, which matters because these get printed small
// and scanned off paper in a noisy restaurant.

import qrcodeGenerator from 'qrcode-generator';

export interface QrOptions {
  /** Device pixels per QR module. 8 gives ~300px for a short URL. */
  scale?: number;
  /** Quiet-zone width in modules. The spec requires 4; never go below it. */
  margin?: number;
}

/**
 * Render `text` as a PNG data URL.
 *
 * Error-correction level M (~15% recovery) is the right trade for a printed
 * card: H would survive more damage but packs the modules tighter, which hurts
 * more than it helps at this size.
 */
export function qrPngDataUrl(text: string, options: QrOptions = {}): string {
  const scale = Math.max(1, Math.floor(options.scale ?? 8));
  const margin = Math.max(4, Math.floor(options.margin ?? 4));

  // typeNumber 0 = auto-size to the shortest version that fits.
  const qr = qrcodeGenerator(0, 'M');
  qr.addData(text);
  qr.make();

  const count = qr.getModuleCount();
  const size = (count + margin * 2) * scale;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable — cannot render the QR code.');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#000000';
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (!qr.isDark(row, col)) continue;
      ctx.fillRect((col + margin) * scale, (row + margin) * scale, scale, scale);
    }
  }
  return canvas.toDataURL('image/png');
}

/** The URL a claim QR encodes: the console's claim route, code pre-filled. */
export function claimUrl(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}/#/claim?code=${encodeURIComponent(code)}`;
}
